'use client';

// ─────────────────────────────────────────────────────────────────────────────
// DashboardInteractive — orchestrator for the PM dashboard.
//
// Previously a 420-line long-scroll page that rendered every widget in a
// single column. After the 6-tab restructure this file is a thin shell that:
//
//   - owns activeTab state (default: 'today')
//   - owns modal state for the 3 iframe slideovers (job-detail, escalation,
//     employee-directory) — same iframe pattern as before, forwarded to
//     tabs that need to trigger them
//   - mounts ALL six tab panels at once and toggles visibility with
//     `display: none` so each composed widget's data fetch + realtime
//     subscription survives tab switches (no remount, no loading flash)
//   - reserves bottom padding equal to the BottomTabBar height so content
//     never gets hidden behind the fixed nav
//
// Why always-mount + display:none rather than lifting subscriptions up?
//   Each widget (TicketsWidget, ActiveJobsGrid, VirtualMeetingsWidget,
//   CalendarWidget) currently owns its data fetch, realtime channel, demo
//   fallback, AbortController-style cleanup, and error UI. Lifting all
//   four to the orchestrator would be a 500+ LOC refactor on top of this
//   PR's existing scope. Always-mount achieves the same UX — instant tab
//   switches, no subscription handshake delay — at zero risk to the widget
//   contracts. TODO(perf): once the dashboard ships and we have a baseline,
//   consider lifting subscriptions to a single PMDashboardContext.
// ─────────────────────────────────────────────────────────────────────────────

import { useCallback, useEffect, useState } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import { useAuth } from '@/contexts/AuthContext';

import TopAppBar from './TopAppBar';
import BottomTabBar, { TAB_PANEL_ID, type TabKey } from './BottomTabBar';
import TodayTab from './tabs/TodayTab';
import JobsTab from './tabs/JobsTab';
import ScheduleTab from './tabs/ScheduleTab';
import InspectionsTab from './tabs/InspectionsTab';
import TicketsTab from './tabs/TicketsTab';
import DocumentsTab from './tabs/DocumentsTab';

type ModalType = 'job-detail' | 'escalation' | 'employee-directory' | null;

interface SlideModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  href: string;
  icon: string;
  iconColor: string;
}

function SlideModal({ isOpen, onClose, title, href, icon, iconColor }: SlideModalProps) {
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  return (
    <>
      <div
        className={`fixed inset-0 z-[60] bg-black/50 backdrop-blur-sm transition-opacity duration-300 ${
          isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        className={`fixed inset-y-0 right-0 z-[70] w-full max-w-5xl bg-background shadow-2xl flex flex-col transition-transform duration-300 ease-in-out ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-border bg-card flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${iconColor}`}>
              <Icon name={icon} size={16} className="text-white" variant="solid" />
            </div>
            <h2 className="font-heading text-base font-semibold text-foreground">{title}</h2>
          </div>
          <div className="flex items-center gap-2">
            <Link
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 px-3 bg-muted text-muted-foreground hover:text-foreground rounded-lg font-caption text-xs transition-standard"
              style={{ minHeight: '44px' }}
              title="Open in new tab"
            >
              <Icon name="ArrowTopRightOnSquareIcon" size={13} variant="outline" />
              Open full page
            </Link>
            <button
              onClick={onClose}
              className="flex items-center justify-center rounded-lg hover:bg-muted transition-standard"
              style={{ minWidth: '44px', minHeight: '44px' }}
              aria-label="Close panel"
            >
              <Icon name="XMarkIcon" size={18} className="text-muted-foreground" />
            </button>
          </div>
        </div>
        <div className="flex-1 overflow-hidden">
          {isOpen && (
            <iframe
              src={href}
              className="w-full h-full border-0"
              title={title}
              loading="lazy"
            />
          )}
        </div>
      </div>
    </>
  );
}

const TAB_TITLES: Record<TabKey, string> = {
  today: 'Today',
  jobs: 'Jobs',
  schedule: 'Schedule',
  inspections: 'Inspections',
  tickets: 'Tickets & Escalations',
  documents: 'Documents',
};

interface TabPanelProps {
  active: boolean;
  tabKey: TabKey;
  children: React.ReactNode;
}

function TabPanel({ active, tabKey, children }: TabPanelProps) {
  return (
    <div
      role="tabpanel"
      id={TAB_PANEL_ID[tabKey]}
      aria-labelledby={`pm-tab-button-${tabKey}`}
      hidden={!active}
      style={{ display: active ? 'block' : 'none' }}
    >
      {children}
    </div>
  );
}

export default function DashboardInteractive() {
  const { currentUser } = useAuth();
  const [activeTab, setActiveTab] = useState<TabKey>('today');
  const [activeModal, setActiveModal] = useState<ModalType>(null);

  const openJobDetail = useCallback(() => setActiveModal('job-detail'), []);
  const openEscalation = useCallback(() => setActiveModal('escalation'), []);
  const openEmployeeDirectory = useCallback(() => setActiveModal('employee-directory'), []);
  const closeModal = useCallback(() => setActiveModal(null), []);

  return (
    // pb-[80px] keeps the last bit of tab content above the 64px BottomTabBar
    // plus a bit of breathing room. iOS safe-area-inset is layered inside
    // BottomTabBar itself, so the gutter is automatic on devices that need it.
    <div className="p-4 sm:p-6 pb-[80px]">
      <TopAppBar
        firstName={currentUser.first_name}
        tabTitle={TAB_TITLES[activeTab]}
        onBackToHome={activeTab === 'today' ? undefined : () => setActiveTab('today')}
      />

      <TabPanel active={activeTab === 'today'} tabKey="today">
        <TodayTab onSwitchTab={setActiveTab} />
      </TabPanel>

      <TabPanel active={activeTab === 'jobs'} tabKey="jobs">
        <JobsTab onJobDetail={openJobDetail} onEmployeeDirectory={openEmployeeDirectory} />
      </TabPanel>

      <TabPanel active={activeTab === 'schedule'} tabKey="schedule">
        <ScheduleTab />
      </TabPanel>

      <TabPanel active={activeTab === 'inspections'} tabKey="inspections">
        <InspectionsTab />
      </TabPanel>

      <TabPanel active={activeTab === 'tickets'} tabKey="tickets">
        <TicketsTab onEscalation={openEscalation} />
      </TabPanel>

      <TabPanel active={activeTab === 'documents'} tabKey="documents">
        <DocumentsTab onJobDetail={openJobDetail} />
      </TabPanel>

      <BottomTabBar activeTab={activeTab} onTabChange={setActiveTab} />

      {/* ── Modal Overlays ── */}
      <SlideModal
        isOpen={activeModal === 'job-detail'}
        onClose={closeModal}
        title="Job Detail Management"
        href="/job-detail-management-panel?embed=1"
        icon="ClipboardDocumentListIcon"
        iconColor="bg-warning"
      />
      <SlideModal
        isOpen={activeModal === 'escalation'}
        onClose={closeModal}
        title="Escalation Management"
        href="/escalation-management-dashboard?embed=1"
        icon="ExclamationTriangleIcon"
        iconColor="bg-error"
      />
      <SlideModal
        isOpen={activeModal === 'employee-directory'}
        onClose={closeModal}
        title="Employee Directory"
        href="/employee-directory?embed=1"
        icon="UsersIcon"
        iconColor="bg-primary"
      />
    </div>
  );
}
